import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "../../lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", inquiry_type: "table", guests: "2", date: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[10px] font-medium uppercase tracking-[0.16em] text-white/80">{label}</span>
    {children}
  </label>
);

export const ContactForm = () => {
  const { t, lang } = useLang();
  const f = t.form;
  const [v, setV] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!v.name.trim() || !v.phone.trim()) return toast.error(f.required);
    setState("sending");
    try {
      await axios.post(`${API}/contact`, { ...v, guests: Number(v.guests) || null, email: v.email || null, date: v.date || null, language: lang });
      setState("done");
      setV(EMPTY);
      toast.success(f.success);
    } catch {
      setState("idle");
      toast.error(f.error);
    }
  };

  return (
    <form data-testid="hero-reservation-form" onSubmit={submit} className="relative mx-auto w-full max-w-[340px] rounded-[20px] border border-white/25 bg-white/10 p-4 sm:p-5 shadow-[0_30px_60px_-20px_rgba(20,4,8,0.5)] backdrop-blur-md lg:ml-auto lg:mr-0">
      <div className="arch-top absolute -top-px left-1/2 h-1.5 w-16 -translate-x-1/2 rounded-b-full bg-[#E9C46A]" />
      <h3 className="font-display text-xl leading-tight text-white">{f.title}</h3>
      <p className="mt-0.5 text-xs text-white/80">{f.sub}</p>
      {state === "done" ? (
        <div data-testid="form-success-message" className="mt-4 flex flex-col items-center py-5 text-center">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#721121] text-white"><Check /></span>
          <p className="font-display mt-3 text-lg text-white">{f.success}</p>
          <button type="button" data-testid="form-reset-button" onClick={() => setState("idle")} className="link-underline mt-3 text-sm text-[#E9C46A]">↺</button>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="col-span-1"><Field label={f.name}><input data-testid="form-name-input" className="lb-glass-input" value={v.name} onChange={set("name")} autoComplete="name" /></Field></div>
          <div className="col-span-1"><Field label={f.phone}><input data-testid="form-phone-input" className="lb-glass-input" type="tel" value={v.phone} onChange={set("phone")} autoComplete="tel" /></Field></div>
          <div className="col-span-2"><Field label={f.email}><input data-testid="form-email-input" className="lb-glass-input" type="email" value={v.email} onChange={set("email")} autoComplete="email" /></Field></div>
          <div className="col-span-1">
            <Field label={f.type}>
              <select data-testid="form-type-select" className="lb-glass-input" value={v.inquiry_type} onChange={set("inquiry_type")}>
                {Object.entries(f.types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-span-1"><Field label={f.guests}><input data-testid="form-guests-input" className="lb-glass-input" type="number" min="1" max="60" value={v.guests} onChange={set("guests")} /></Field></div>
          <div className="col-span-2"><Field label={f.date}><input data-testid="form-date-input" className="lb-glass-input" type="date" value={v.date} onChange={set("date")} /></Field></div>
          <div className="col-span-2"><Field label={f.message}><textarea data-testid="form-message-input" className="lb-glass-input h-9 resize-none" value={v.message} onChange={set("message")} /></Field></div>
          <button data-testid="hero-reservation-submit-button" disabled={state === "sending"} className="btn-wine group col-span-2 mt-1 w-full justify-center py-2.5 text-sm">
            {state === "sending" ? f.sending : f.submit}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      )}
    </form>
  );
};
